/**
 * EGPresets - the genres: a whole rig per style, a riff to hear it on, and a
 * band to play with.
 *
 * WHY WHOLE RIGS
 * --------------
 * Every professional preset library (Spark, BOSS Tone Central, Guitar Rig,
 * AmpliTube) is organised the same way: genre is the entry point, gain
 * character the variant. A genre here sets EVERYTHING that makes the style -
 * the guitar and pickup, the tuning, the pedals, the amp and its knobs, the
 * cabinet, the effects, the tempo and the groove - because that is what a
 * player means by "the grunge sound": drop D and a fuzz pedal into a clean-ish
 * amp, not an amp model with a name on it.
 *
 * Names are styles and the gear archetypes are described in words, never as
 * trademarks or songs (and every riff here is original: rock vocabulary -
 * power chords, pentatonic boxes, the boogie shuffle, the gallop - belongs to
 * nobody, a specific riff does).
 *
 * RIFF NOTATION
 * -------------
 * [beat, string, fret, length, flags]
 *   string  1 = high E ... 6 = low E (7 = low B on a seven-string)
 *   flags   pm palm mute · x dead note · 5 power chord (root + fifth + octave)
 *           b1/b2 bend up a semitone/tone · v vibrato · s<n> slide to fret n
 *           h hammer-on/pull-off (legato) · ph pinch harmonic
 *           nh natural harmonic · u upstroke · t tremolo picking
 *           c:<frets> full chord, low string first ("x02220")
 */
(function (root, factory) {
    var api = factory();
    if (typeof globalThis !== 'undefined') globalThis.EGPresets = api;
    if (typeof window !== 'undefined') window.EGPresets = api;
}(this, function () {
    'use strict';

    // ───────────────────────────── grooves ─────────────────────────────────
    /* 16 steps per bar (12 for the triplet feels). Pieces are the VCSL kit
       of /virtual-drums (CC0), shared file-for-file with that tool. */
    var GROOVES = {
        rock8: { steps: 16, k: 'x.......x.x.....', s: '....x.......x...', h: 'x.x.x.x.x.x.x.x.' },
        rock8b: { steps: 16, k: 'x.....x.x.....x.', s: '....x.......x...', h: 'x.x.x.x.x.x.x.x.' },
        half: { steps: 16, k: 'x.......x.......', s: '........x.......', h: 'x.x.x.x.x.x.x.x.' },
        punk: { steps: 16, k: 'x...x...x...x...', s: '..x...x...x...x.', h: 'x.x.x.x.x.x.x.x.' },
        gallop: { steps: 16, k: 'x.xxx.xxx.xxx.xx', s: '....x.......x...', r: 'x...x...x...x...' },
        thrash: { steps: 16, k: 'x.x.x.x.x.x.x.x.', s: '..x...x...x...x.', r: 'x...x...x...x...' },
        djent: { steps: 16, k: 'x..x..x.x..x..x.', s: '........x.......', r: 'x...x...x...x...' },
        shuffle: { steps: 12, k: 'x.....x.....', s: '...x.....x..', h: 'x.xx.xx.xx.x' },
        swing: { steps: 12, k: 'x.....x.....', s: '...x.....x..', r: 'x.xx.xx.xx.x' },
        surf: { steps: 16, k: 'x.......x.......', s: '....x.......x...', t3: 'x.x.x.x.x.x.x.x.' },
        funk: { steps: 16, k: 'x..x..x...x..x..', s: '....x..g.g..x..g', h: 'xxxxxxxxxxxxxxxx' },
        ballad: { steps: 16, k: 'x.......x.......', s: '....x.......x...', r: 'x.x.x.x.x.x.x.x.' },
        doom: { steps: 16, k: 'x.......x.......', s: '........x.......', c: 'x...............' },
        motorik: { steps: 16, k: 'x...x...x...x...', s: '....x.......x...', h: 'x.x.x.x.x.x.x.x.' }
    };

    // ─────────────────────────── riff helpers ──────────────────────────────
    function R(bpm, beats, notes, opt) {
        return Object.assign({ bpm: bpm, beats: beats, notes: notes }, opt || {});
    }

    // ───────────────────────────── the genres ──────────────────────────────
    /* `trim` (dB) is MEASURED, not chosen: each preset's own riff through its
       own rig, levelled to -14 dB RMS (tests/audio preset render). Without it
       the clean genres arrived 12 dB quieter than the fuzz ones and every
       genre change was a volume jump first. */
    /*
     * gain ladder, left to right: clean, crunch, drive, high gain.
     * amp models: clean (US black-panel), tweed (US 50s), chime (UK class A),
     *   plexi (UK 60s), brit800 (UK 80s master volume), modern (US high gain).
     * cabs: v30, greenback, dv77, g12f (closed 4x12, measured CC0 captures),
     *   2x12o, 1x12o (open-back).
     */
    var P = [
        {
            id: 'classic-rock', group: 'crunch', hue: 18,
            guitar: { model: 'lp', sel: 'b', tone: 0.85, vol: 1, tuning: 'std', pickMm: 85, sympathy: 0.35 },
            pedals: { comp: { on: false }, wah: { on: false }, boost: { on: false }, drive: { on: false, type: 'od', drive: 0.3, tone: 0.5, level: 0.6 } },
            amp: { model: 'plexi', gain: 0.62, bass: 0.55, mid: 0.7, treble: 0.62, presence: 0.55, master: 0.7, res: 0.3 },
            cab: { type: 'greenback', mic: 0.4 },
            mod: { on: false }, delay: { on: true, sync: '4', fb: 0.18, mix: 0.1, type: 'tape' },
            reverb: { on: true, type: 'room', decay: 0.4, mix: 0.14 }, gate: -80, trim: -2.0,
            jam: { groove: 'rock8', bpm: 124, key: 'A', scale: 'minpent', prog: [['I5', 2], ['bVII5', 1], ['IV5', 1]] },
            riff: R(124, 8, [
                [0, 6, 5, 0.5, 'pm 5'], [0.5, 6, 5, 0.5, 'pm 5'], [1, 6, 8, 0.5, '5'], [1.5, 6, 5, 0.5, 'pm 5'],
                [2, 5, 5, 1, '5'], [3, 6, 8, 0.5, '5'], [3.5, 6, 5, 0.5, '5'],
                [4, 6, 5, 0.5, 'pm 5'], [4.5, 6, 5, 0.5, 'pm 5'], [5, 6, 3, 0.5, '5'], [5.5, 6, 5, 0.5, 'pm 5'],
                [6, 4, 7, 0.25], [6.25, 4, 5, 0.25], [6.5, 5, 7, 0.25], [6.75, 5, 5, 0.25],
                [7, 6, 8, 0.25], [7.25, 6, 5, 0.25], [7.5, 6, 5, 0.5, '5']
            ])
        },
        {
            id: 'hard-rock', group: 'drive', hue: 4,
            guitar: { model: 'ss', sel: 'b', tone: 0.9, vol: 1, tuning: 'std', pickMm: 70, sympathy: 0.2 },
            pedals: { comp: { on: false }, wah: { on: false }, boost: { on: true, drive: 0.2, level: 0.55 }, drive: { on: false, type: 'od' } },
            amp: { model: 'brit800', gain: 0.62, bass: 0.55, mid: 0.65, treble: 0.6, presence: 0.55, master: 0.6, res: 0.4 },
            cab: { type: 'v30', mic: 0.45 },
            mod: { on: false }, delay: { on: true, sync: '8.', fb: 0.2, mix: 0.1, type: 'analog' },
            reverb: { on: true, type: 'plate', decay: 0.35, mix: 0.12 }, gate: -66, trim: -0.5,
            jam: { groove: 'rock8b', bpm: 132, key: 'E', scale: 'minpent', prog: [['I5', 2], ['bVI5', 1], ['bVII5', 1]] },
            riff: R(132, 8, [
                [0, 6, 0, 0.5, 'pm 5'], [0.5, 6, 0, 0.5, 'pm 5'], [1, 6, 0, 0.5, 'pm 5'], [1.5, 6, 3, 0.5, '5'],
                [2, 6, 0, 0.5, 'pm 5'], [2.5, 6, 0, 0.5, 'pm 5'], [3, 6, 5, 0.5, '5'], [3.5, 6, 3, 0.5, '5'],
                [4, 6, 0, 0.5, 'pm 5'], [4.5, 6, 0, 0.5, 'pm 5'], [5, 6, 0, 0.5, 'pm 5'], [5.5, 5, 5, 0.5, '5'],
                [6, 5, 3, 1, '5'], [7, 5, 2, 0.5, '5'], [7.5, 6, 0, 0.5, '5 v']
            ])
        },
        {
            id: 'heavy-metal', group: 'high', hue: 350,
            guitar: { model: 'lp', sel: 'b', tone: 0.9, vol: 1, tuning: 'std', pickMm: 60, sympathy: 0.1 },
            pedals: { comp: { on: false }, wah: { on: false }, boost: { on: true, drive: 0.1, level: 0.7 }, drive: { on: false, type: 'od' } },
            amp: { model: 'modern', gain: 0.55, bass: 0.6, mid: 0.55, treble: 0.62, presence: 0.6, master: 0.55, res: 0.5 },
            cab: { type: 'v30', mic: 0.4 },
            mod: { on: false }, delay: { on: false, sync: '4', fb: 0.2, mix: 0.1 },
            reverb: { on: true, type: 'room', decay: 0.35, mix: 0.1 }, gate: -60, trim: -0.1,
            jam: { groove: 'gallop', bpm: 168, key: 'E', scale: 'minor', prog: [['I5', 2], ['bVI5', 1], ['bVII5', 1]] },
            riff: R(168, 8, [
                [0, 6, 0, 0.5, 'pm'], [0.5, 6, 0, 0.25, 'pm'], [0.75, 6, 0, 0.25, 'pm'],
                [1, 6, 0, 0.5, 'pm'], [1.5, 6, 0, 0.25, 'pm'], [1.75, 6, 0, 0.25, 'pm'],
                [2, 6, 0, 0.5, 'pm'], [2.5, 6, 0, 0.25, 'pm'], [2.75, 6, 0, 0.25, 'pm'],
                [3, 6, 3, 0.5, '5'], [3.5, 6, 1, 0.5, '5'],
                [4, 6, 0, 0.5, 'pm'], [4.5, 6, 0, 0.25, 'pm'], [4.75, 6, 0, 0.25, 'pm'],
                [5, 6, 0, 0.5, 'pm'], [5.5, 6, 0, 0.25, 'pm'], [5.75, 6, 0, 0.25, 'pm'],
                [6, 6, 5, 0.5, '5'], [6.5, 6, 3, 0.5, '5'], [7, 6, 1, 0.5, '5'], [7.5, 6, 0, 0.5, '5 v']
            ])
        },
        {
            id: 'thrash', group: 'high', hue: 0,
            guitar: { model: 'ss', sel: 'b', tone: 0.9, vol: 1, tuning: 'std', pickMm: 50, sympathy: 0.05 },
            pedals: { comp: { on: false }, wah: { on: false }, boost: { on: true, drive: 0.05, level: 0.75 }, drive: { on: false, type: 'od' } },
            amp: { model: 'modern', gain: 0.62, bass: 0.62, mid: 0.3, treble: 0.68, presence: 0.62, master: 0.55, res: 0.55 },
            cab: { type: 'dv77', mic: 0.35 },
            mod: { on: false }, delay: { on: false },
            reverb: { on: true, type: 'room', decay: 0.3, mix: 0.08 }, gate: -56, trim: -0.9,
            jam: { groove: 'thrash', bpm: 200, key: 'E', scale: 'phrygian', prog: [['I5', 2], ['bII5', 1], ['I5', 1]] },
            riff: R(200, 8, [
                [0, 6, 0, 0.5, 'pm'], [0.5, 6, 0, 0.5, 'pm'], [1, 6, 0, 0.5, 'pm'], [1.5, 6, 1, 0.5, '5'],
                [2, 6, 0, 0.5, 'pm'], [2.5, 6, 0, 0.5, 'pm'], [3, 6, 0, 0.5, 'pm'], [3.5, 6, 3, 0.5, '5'],
                [4, 6, 0, 0.5, 'pm'], [4.5, 6, 0, 0.5, 'pm'], [5, 6, 0, 0.5, 'pm'], [5.5, 6, 6, 0.5, '5'],
                [6, 6, 5, 0.5, '5'], [6.5, 6, 3, 0.5, '5'], [7, 6, 1, 0.5, '5'], [7.5, 6, 0, 0.5, '5']
            ])
        },
        {
            id: 'modern-metal', group: 'high', hue: 280,
            guitar: { model: 's7', sel: 'b', tone: 0.95, vol: 1, tuning: 'b7', pickMm: 45, sympathy: 0 },
            pedals: { comp: { on: false }, wah: { on: false }, boost: { on: true, drive: 0.05, level: 0.8 }, drive: { on: false, type: 'od' } },
            amp: { model: 'modern', gain: 0.6, bass: 0.55, mid: 0.6, treble: 0.6, presence: 0.62, master: 0.5, res: 0.5 },
            cab: { type: 'v30', mic: 0.45 },
            mod: { on: false }, delay: { on: true, sync: '4', fb: 0.15, mix: 0.07, type: 'digital' },
            reverb: { on: true, type: 'plate', decay: 0.4, mix: 0.1 }, gate: -52, trim: 0.8,
            jam: { groove: 'djent', bpm: 110, key: 'B', scale: 'minor', prog: [['I5', 2], ['bVI5', 1], ['bVII5', 1]] },
            riff: R(110, 8, [
                [0, 7, 0, 0.25, 'pm'], [0.75, 7, 0, 0.25, 'pm'], [1.5, 7, 0, 0.25, 'pm'], [2, 7, 0, 0.25, 'pm'],
                [2.75, 7, 0, 0.25, 'pm'], [3, 3, 11, 1, 'v'],
                [4, 7, 0, 0.25, 'pm'], [4.75, 7, 0, 0.25, 'pm'], [5.5, 7, 0, 0.25, 'pm'], [6, 7, 1, 0.5, '5'],
                [6.5, 7, 0, 0.25, 'pm'], [7, 7, 3, 1, '5']
            ])
        },
        {
            id: 'punk', group: 'drive', hue: 330,
            guitar: { model: 'lp', sel: 'b', tone: 0.9, vol: 1, tuning: 'std', pickMm: 90, sympathy: 0.15 },
            pedals: { comp: { on: false }, wah: { on: false }, boost: { on: false }, drive: { on: false, type: 'ds' } },
            amp: { model: 'brit800', gain: 0.5, bass: 0.6, mid: 0.6, treble: 0.62, presence: 0.5, master: 0.65, res: 0.35 },
            cab: { type: 'v30', mic: 0.5 },
            mod: { on: false }, delay: { on: false },
            reverb: { on: true, type: 'room', decay: 0.3, mix: 0.08 }, gate: -66, trim: -2.0,
            jam: { groove: 'punk', bpm: 180, key: 'D', scale: 'major', prog: [['I5', 1], ['V5', 1], ['vi5', 1], ['IV5', 1]] },
            riff: R(180, 8, [
                [0, 5, 5, 0.5, '5'], [0.5, 5, 5, 0.5, '5'], [1, 5, 5, 0.5, '5'], [1.5, 5, 5, 0.5, '5'],
                [2, 6, 5, 0.5, '5'], [2.5, 6, 5, 0.5, '5'], [3, 6, 5, 0.5, '5'], [3.5, 6, 5, 0.5, '5'],
                [4, 5, 2, 0.5, '5'], [4.5, 5, 2, 0.5, '5'], [5, 5, 2, 0.5, '5'], [5.5, 5, 2, 0.5, '5'],
                [6, 6, 3, 0.5, '5'], [6.5, 6, 3, 0.5, '5'], [7, 6, 3, 0.5, '5'], [7.5, 6, 5, 0.5, '5']
            ])
        },
        {
            id: 'pop-punk', group: 'drive', hue: 312,
            guitar: { model: 'ss', sel: 'b', tone: 0.9, vol: 1, tuning: 'dropd', pickMm: 80, sympathy: 0.1 },
            pedals: { comp: { on: false }, wah: { on: false }, boost: { on: true, drive: 0.15, level: 0.55 }, drive: { on: false, type: 'od' } },
            amp: { model: 'brit800', gain: 0.62, bass: 0.6, mid: 0.55, treble: 0.62, presence: 0.55, master: 0.6, res: 0.4 },
            cab: { type: 'v30', mic: 0.45 },
            mod: { on: false }, delay: { on: false },
            reverb: { on: true, type: 'room', decay: 0.35, mix: 0.1 }, gate: -60, trim: -0.5,
            jam: { groove: 'punk', bpm: 172, key: 'D', scale: 'major', prog: [['I5', 1], ['V5', 1], ['vi5', 1], ['IV5', 1]] },
            riff: R(172, 8, [
                [0, 6, 0, 0.5, 'pm 5'], [0.5, 6, 0, 0.5, 'pm 5'], [1, 6, 0, 0.5, 'pm 5'], [1.5, 6, 0, 0.5, 'pm 5'],
                [2, 6, 7, 0.5, 'pm 5'], [2.5, 6, 7, 0.5, 'pm 5'], [3, 6, 7, 0.5, '5'], [3.5, 6, 7, 0.5, '5'],
                [4, 6, 9, 0.5, 'pm 5'], [4.5, 6, 9, 0.5, 'pm 5'], [5, 6, 9, 0.5, '5'], [5.5, 6, 9, 0.5, '5'],
                [6, 6, 5, 1, '5'], [7, 6, 5, 0.5, '5'], [7.5, 6, 7, 0.5, '5']
            ])
        },
        {
            id: 'grunge', group: 'fuzz', hue: 36,
            guitar: { model: 'lp', sel: 'b', tone: 0.8, vol: 1, tuning: 'dropd', pickMm: 95, sympathy: 0.3 },
            pedals: { comp: { on: false }, wah: { on: false }, boost: { on: false }, drive: { on: true, type: 'ds', drive: 0.75, tone: 0.45, level: 0.62 } },
            amp: { model: 'plexi', gain: 0.35, bass: 0.6, mid: 0.55, treble: 0.55, presence: 0.45, master: 0.6, res: 0.4 },
            cab: { type: 'greenback', mic: 0.45 },
            mod: { on: false, type: 'chorus', rate: 0.3, depth: 0.5, mix: 0.4 }, delay: { on: false },
            reverb: { on: true, type: 'room', decay: 0.45, mix: 0.12 }, gate: -62, trim: -5.9,
            jam: { groove: 'rock8', bpm: 108, key: 'D', scale: 'minpent', prog: [['I5', 2], ['bIII5', 1], ['IV5', 1]] },
            riff: R(108, 8, [
                [0, 6, 0, 1, '5'], [1, 6, 0, 0.5, 'pm 5'], [1.5, 6, 3, 0.5, '5'], [2, 6, 5, 1, '5'],
                [3, 6, 0, 0.25, 'x'], [3.25, 6, 0, 0.25, 'x'], [3.5, 6, 3, 0.5, '5 s1'],
                [4, 6, 0, 1, '5'], [5, 6, 0, 0.5, 'pm 5'], [5.5, 6, 3, 0.5, '5'], [6, 6, 5, 0.5, '5'], [6.5, 6, 6, 0.5, '5'],
                [7, 6, 5, 1, '5 v']
            ])
        },
        {
            id: 'alt-indie', group: 'crunch', hue: 190,
            guitar: { model: 't', sel: 'b', tone: 0.8, vol: 1, tuning: 'std', pickMm: 90, sympathy: 0.55 },
            pedals: { comp: { on: false }, wah: { on: false }, boost: { on: false }, drive: { on: true, type: 'od', drive: 0.25, tone: 0.6, level: 0.55 } },
            amp: { model: 'chime', gain: 0.45, bass: 0.45, mid: 0.6, treble: 0.62, presence: 0.5, master: 0.6, res: 0.2 },
            cab: { type: '2x12o', mic: 0.4 },
            mod: { on: false }, delay: { on: true, sync: '8.', fb: 0.3, mix: 0.2, type: 'digital' },
            reverb: { on: true, type: 'room', decay: 0.5, mix: 0.16 }, gate: -80, trim: 4.8,
            jam: { groove: 'rock8', bpm: 128, key: 'E', scale: 'major', prog: [['I', 1], ['vi', 1], ['IV', 1], ['V', 1]] },
            riff: R(128, 8, [
                [0, 4, 2, 0.5], [0.5, 2, 0, 0.5], [1, 3, 4, 0.5], [1.5, 1, 0, 0.5],
                [2, 4, 2, 0.5], [2.5, 2, 0, 0.5], [3, 3, 2, 0.5], [3.5, 1, 0, 0.5],
                [4, 4, 4, 0.5], [4.5, 2, 0, 0.5], [5, 3, 4, 0.5], [5.5, 1, 0, 0.5],
                [6, 4, 2, 0.5], [6.5, 2, 0, 0.5], [7, 3, 1, 0.5, 'h'], [7.5, 3, 2, 0.5, 'h']
            ])
        },
        {
            id: 'blues-rock', group: 'crunch', hue: 220,
            guitar: { model: 's', sel: 'n', tone: 0.75, vol: 1, tuning: 'std', pickMm: 110, sympathy: 0.5 },
            pedals: { comp: { on: false }, wah: { on: false }, boost: { on: false }, drive: { on: true, type: 'od', drive: 0.45, tone: 0.55, level: 0.65 } },
            amp: { model: 'tweed', gain: 0.5, bass: 0.5, mid: 0.6, treble: 0.6, presence: 0.5, master: 0.65, res: 0.3 },
            cab: { type: '2x12o', mic: 0.45 },
            mod: { on: false }, delay: { on: false },
            reverb: { on: true, type: 'spring', decay: 0.45, mix: 0.18 }, gate: -80, trim: -2.6,
            jam: { groove: 'shuffle', bpm: 96, key: 'A', scale: 'blues', prog: [['I7', 4], ['IV7', 2], ['I7', 2], ['V7', 1], ['IV7', 1], ['I7', 1], ['V7', 1]] },
            riff: R(96, 8, [
                [0, 5, 0, 0.667, '5'], [0.667, 5, 0, 0.333, '5'], [1, 5, 0, 0.667, 'c:x04'], [1.667, 5, 0, 0.333, 'c:x04'],
                [2, 5, 0, 0.667, '5'], [2.667, 5, 0, 0.333, '5'], [3, 5, 0, 0.667, 'c:x04'], [3.667, 5, 0, 0.333, 'c:x04'],
                [4, 2, 8, 0.667, 'b2'], [4.667, 1, 5, 0.333], [5, 2, 8, 0.333], [5.333, 2, 5, 0.333], [5.667, 3, 7, 0.333, 'h'],
                [6, 3, 5, 0.667], [6.667, 4, 7, 0.333], [7, 4, 5, 1, 'v']
            ], { feel: 'shuffle' })
        },
        {
            id: 'surf', group: 'clean', hue: 170,
            guitar: { model: 'o', sel: 'b', tone: 0.85, vol: 1, tuning: 'std', pickMm: 60, sympathy: 0.4 },
            pedals: { comp: { on: false }, wah: { on: false }, boost: { on: false }, drive: { on: false } },
            amp: { model: 'clean', gain: 0.45, bass: 0.45, mid: 0.5, treble: 0.65, presence: 0.55, master: 0.6, res: 0.2 },
            cab: { type: '2x12o', mic: 0.35 },
            mod: { on: false, type: 'tremolo', rate: 0.5, depth: 0.5 }, delay: { on: false },
            reverb: { on: true, type: 'spring', decay: 0.8, mix: 0.42 }, gate: -80, trim: 8.3,
            jam: { groove: 'surf', bpm: 176, key: 'E', scale: 'phrygdom', prog: [['I', 2], ['bII', 1], ['I', 1]] },
            riff: R(176, 8, [
                [0, 6, 0, 1, 't'], [1, 6, 4, 1, 't'], [2, 6, 1, 1, 't'], [3, 6, 0, 1, 't'],
                [4, 6, 5, 1, 't'], [5, 6, 4, 0.5, 't'], [5.5, 6, 1, 0.5, 't'], [6, 6, 0, 2, 't']
            ])
        },
        {
            id: 'psychedelic', group: 'fuzz', hue: 300,
            guitar: { model: 's', sel: 'n', tone: 0.8, vol: 1, tuning: 'eb', pickMm: 100, sympathy: 0.4 },
            pedals: { comp: { on: false }, wah: { on: true, mode: 'auto', sens: 0.55 }, boost: { on: false }, drive: { on: true, type: 'ge', drive: 0.7, tone: 0.55, level: 0.6 } },
            amp: { model: 'plexi', gain: 0.35, bass: 0.55, mid: 0.6, treble: 0.6, presence: 0.5, master: 0.65, res: 0.3 },
            cab: { type: 'greenback', mic: 0.4 },
            mod: { on: true, type: 'vibe', rate: 0.35, depth: 0.6, mix: 1 }, delay: { on: true, sync: '4', fb: 0.35, mix: 0.2, type: 'tape' },
            reverb: { on: true, type: 'plate', decay: 0.5, mix: 0.16 }, gate: -76, trim: -3.1,
            jam: { groove: 'rock8b', bpm: 100, key: 'E', scale: 'minpent', prog: [['I7', 2], ['bVII', 1], ['IV', 1]] },
            riff: R(100, 8, [
                [0, 6, 0, 0.5, '5'], [0.5, 4, 2, 0.5], [1, 4, 5, 0.5, 'b2'], [1.5, 4, 2, 0.5],
                [2, 5, 2, 0.5], [2.5, 5, 0, 0.5], [3, 6, 3, 0.5], [3.5, 6, 2, 0.5, 'h'],
                [4, 6, 0, 0.5, '5'], [4.5, 3, 2, 0.5], [5, 3, 4, 1, 'b2 v'], [6, 3, 2, 0.5], [6.5, 4, 2, 0.5], [7, 4, 0, 1, 'v']
            ])
        },
        {
            id: 'stoner-doom', group: 'fuzz', hue: 40,
            guitar: { model: 'lp', sel: 'b', tone: 0.7, vol: 1, tuning: 'dropc', pickMm: 100, sympathy: 0.2 },
            pedals: { comp: { on: false }, wah: { on: false }, boost: { on: false }, drive: { on: true, type: 'fz', drive: 0.7, tone: 0.4, level: 0.62 } },
            amp: { model: 'plexi', gain: 0.45, bass: 0.7, mid: 0.6, treble: 0.5, presence: 0.4, master: 0.7, res: 0.6 },
            cab: { type: 'g12f', mic: 0.55 },
            mod: { on: false }, delay: { on: false },
            reverb: { on: true, type: 'room', decay: 0.55, mix: 0.12 }, gate: -66, trim: -10,
            jam: { groove: 'doom', bpm: 66, key: 'C', scale: 'minpent', prog: [['I5', 2], ['bIII5', 1], ['IV5', 1]] },
            riff: R(66, 8, [
                [0, 6, 0, 2, '5 v'], [2, 6, 3, 1, '5'], [3, 6, 5, 0.5, '5'], [3.5, 6, 3, 0.5, '5'],
                [4, 6, 0, 1.5, '5'], [5.5, 5, 3, 0.5], [6, 5, 5, 1, 'b1 v'], [7, 6, 3, 0.5, '5'], [7.5, 6, 1, 0.5, '5']
            ])
        },
        {
            id: 'shoegaze', group: 'fuzz', hue: 262,
            guitar: { model: 'o', sel: 'nb', tone: 0.7, vol: 1, tuning: 'std', pickMm: 120, sympathy: 0.8 },
            pedals: { comp: { on: false }, wah: { on: false }, boost: { on: false }, drive: { on: true, type: 'fz', drive: 0.55, tone: 0.5, level: 0.55 } },
            amp: { model: 'chime', gain: 0.3, bass: 0.5, mid: 0.5, treble: 0.55, presence: 0.45, master: 0.6, res: 0.3 },
            cab: { type: '2x12o', mic: 0.5 },
            mod: { on: true, type: 'chorus', rate: 0.25, depth: 0.85, mix: 0.6 }, delay: { on: true, sync: '4', fb: 0.4, mix: 0.25, type: 'analog' },
            reverb: { on: true, type: 'huge', decay: 0.8, mix: 0.45 }, gate: -80, trim: -2.4,
            jam: { groove: 'ballad', bpm: 92, key: 'E', scale: 'major', prog: [['I', 1], ['IV', 1], ['vi', 1], ['IV', 1]] },
            riff: R(92, 8, [
                [0, 6, 0, 2, 'c:022100'], [2, 6, 0, 1, 'c:x02200'], [3, 6, 0, 1, 'u c:x02200'],
                [4, 6, 0, 2, 'c:x04420'], [6, 6, 0, 2, 'c:x02220']
            ], { ring: true })
        },
        {
            id: 'post-rock', group: 'clean', hue: 200,
            guitar: { model: 't', sel: 'n', tone: 0.8, vol: 1, tuning: 'std', pickMm: 100, sympathy: 0.7 },
            pedals: { comp: { on: true, sustain: 0.45, level: 0.5 }, wah: { on: false }, boost: { on: false }, drive: { on: false } },
            amp: { model: 'clean', gain: 0.4, bass: 0.5, mid: 0.5, treble: 0.6, presence: 0.5, master: 0.55, res: 0.2 },
            cab: { type: '2x12o', mic: 0.45 },
            mod: { on: false }, delay: { on: true, sync: '8.', fb: 0.45, mix: 0.3, type: 'digital' },
            reverb: { on: true, type: 'hall', decay: 0.75, mix: 0.35 }, gate: -80, trim: 2.8,
            jam: { groove: 'ballad', bpm: 100, key: 'D', scale: 'major', prog: [['I', 1], ['V', 1], ['vi', 1], ['IV', 1]] },
            riff: R(100, 8, [
                [0, 2, 3, 1], [1, 1, 2, 1], [2, 2, 5, 1], [3, 1, 5, 1],
                [4, 2, 3, 1], [5, 1, 2, 1], [6, 2, 7, 1, 'v'], [7, 2, 5, 1]
            ], { ring: true })
        },
        {
            id: 'post-punk', group: 'clean', hue: 150,
            guitar: { model: 's', sel: 'm', tone: 0.85, vol: 1, tuning: 'std', pickMm: 90, sympathy: 0.5 },
            pedals: { comp: { on: true, sustain: 0.4, level: 0.5 }, wah: { on: false }, boost: { on: false }, drive: { on: false } },
            amp: { model: 'clean', gain: 0.55, bass: 0.45, mid: 0.55, treble: 0.65, presence: 0.55, master: 0.6, res: 0.2 },
            cab: { type: '1x12o', mic: 0.4 },
            mod: { on: true, type: 'chorus', rate: 0.35, depth: 0.7, mix: 0.55 }, delay: { on: true, sync: '8', fb: 0.25, mix: 0.18, type: 'digital' },
            reverb: { on: true, type: 'plate', decay: 0.5, mix: 0.2 }, gate: -80, trim: -0.7,
            jam: { groove: 'motorik', bpm: 144, key: 'E', scale: 'minor', prog: [['i', 1], ['bVI', 1], ['bIII', 1], ['bVII', 1]] },
            riff: R(144, 8, [
                [0, 2, 5, 0.5], [0.5, 3, 7, 0.5], [1, 3, 5, 0.5], [1.5, 2, 5, 0.5], [2, 2, 8, 1], [3, 2, 7, 0.5], [3.5, 2, 5, 0.5],
                [4, 2, 5, 0.5], [4.5, 3, 7, 0.5], [5, 3, 5, 0.5], [5.5, 3, 4, 0.5], [6, 3, 5, 1.5, 'v'], [7.5, 4, 7, 0.5]
            ])
        },
        {
            id: 'funk-rock', group: 'clean', hue: 60,
            guitar: { model: 's', sel: 'nm', tone: 0.9, vol: 1, tuning: 'std', pickMm: 100, sympathy: 0.1 },
            pedals: { comp: { on: true, sustain: 0.4, level: 0.55 }, wah: { on: true, mode: 'auto', sens: 0.25 }, boost: { on: false }, drive: { on: false } },
            amp: { model: 'clean', gain: 0.5, bass: 0.45, mid: 0.6, treble: 0.62, presence: 0.55, master: 0.6, res: 0.2 },
            cab: { type: '1x12o', mic: 0.4 },
            mod: { on: false }, delay: { on: false },
            reverb: { on: true, type: 'room', decay: 0.35, mix: 0.1 }, gate: -80, trim: -1.5,
            jam: { groove: 'funk', bpm: 104, key: 'E', scale: 'dorian', prog: [['i9', 2], ['IV9', 2]] },
            riff: R(104, 8, [
                [0, 5, 7, 0.25, 'c:x7677x'], [0.25, 5, 0, 0.25, 'x c:xxxxxx'], [0.5, 5, 7, 0.25, 'u c:x7677x'], [0.75, 5, 0, 0.25, 'x c:xxxxxx'],
                [1, 5, 0, 0.25, 'x c:xxxxxx'], [1.25, 5, 7, 0.25, 'u c:x7677x'], [1.75, 5, 7, 0.25, 'c:x7677x'],
                [2, 5, 0, 0.25, 'x c:xxxxxx'], [2.5, 5, 7, 0.25, 'u c:x7677x'], [2.75, 5, 0, 0.25, 'x c:xxxxxx'], [3, 5, 7, 0.5, 'c:x7677x'],
                [3.5, 5, 9, 0.25, 'c:x9899x'], [3.75, 5, 7, 0.25, 'u c:x7677x'],
                [4, 5, 7, 0.25, 'c:x7677x'], [4.25, 5, 0, 0.25, 'x c:xxxxxx'], [4.5, 5, 7, 0.25, 'u c:x7677x'], [5, 5, 0, 0.25, 'x c:xxxxxx'],
                [5.25, 5, 7, 0.25, 'u c:x7677x'], [5.75, 5, 5, 0.25, 'c:x5455x'], [6, 5, 7, 1, 'c:x7677x'], [7, 5, 0, 0.25, 'x c:xxxxxx'], [7.5, 5, 7, 0.5, 'u c:x7677x']
            ])
        },
        {
            id: 'rockabilly', group: 'crunch', hue: 80,
            guitar: { model: 't', sel: 'nb', tone: 0.8, vol: 1, tuning: 'std', pickMm: 90, sympathy: 0.4 },
            pedals: { comp: { on: true, sustain: 0.35, level: 0.5 }, wah: { on: false }, boost: { on: false }, drive: { on: false } },
            amp: { model: 'tweed', gain: 0.4, bass: 0.5, mid: 0.55, treble: 0.62, presence: 0.55, master: 0.55, res: 0.2 },
            cab: { type: '1x12o', mic: 0.4 },
            mod: { on: false }, delay: { on: true, time: 0.11, fb: 0.05, mix: 0.32, type: 'tape' },
            reverb: { on: true, type: 'spring', decay: 0.35, mix: 0.12 }, gate: -80, trim: 0.6,
            jam: { groove: 'swing', bpm: 168, key: 'E', scale: 'mixolydian', prog: [['I7', 2], ['IV7', 1], ['I7', 1]] },
            riff: R(168, 8, [
                [0, 6, 0, 0.667], [0.667, 6, 4, 0.333], [1, 5, 2, 0.667], [1.667, 5, 4, 0.333],
                [2, 4, 0, 0.667], [2.667, 4, 2, 0.333], [3, 5, 4, 0.667], [3.667, 5, 2, 0.333],
                [4, 6, 0, 0.667], [4.667, 6, 4, 0.333], [5, 5, 2, 0.667], [5.667, 5, 4, 0.333],
                [6, 3, 1, 0.333, 's2'], [6.333, 2, 0, 0.333], [6.667, 1, 0, 0.333], [7, 6, 0, 1, '5 v']
            ], { feel: 'shuffle' })
        },
        {
            id: 'arena-80s', group: 'drive', hue: 244,
            guitar: { model: 'ss', sel: 'b', tone: 0.9, vol: 1, tuning: 'std', pickMm: 80, sympathy: 0.3 },
            pedals: { comp: { on: false }, wah: { on: false }, boost: { on: true, drive: 0.15, level: 0.6 }, drive: { on: false } },
            amp: { model: 'brit800', gain: 0.6, bass: 0.55, mid: 0.6, treble: 0.62, presence: 0.6, master: 0.6, res: 0.4 },
            cab: { type: 'v30', mic: 0.45 },
            mod: { on: true, type: 'chorus', rate: 0.3, depth: 0.55, mix: 0.4 }, delay: { on: true, sync: '4', fb: 0.3, mix: 0.18, type: 'digital' },
            reverb: { on: true, type: 'hall', decay: 0.55, mix: 0.24 }, gate: -62, trim: 0.2,
            jam: { groove: 'rock8', bpm: 128, key: 'A', scale: 'major', prog: [['I', 1], ['V', 1], ['vi', 1], ['IV', 1]] },
            riff: R(128, 8, [
                [0, 5, 0, 0.5, 'pm 5'], [0.5, 5, 0, 0.5, 'pm 5'], [1, 5, 0, 0.5, 'pm 5'], [1.5, 5, 0, 0.5, 'pm 5'],
                [2, 5, 0, 2, 'c:x02220'],
                [4, 6, 0, 0.5, 'pm 5'], [4.5, 6, 0, 0.5, 'pm 5'], [5, 6, 0, 0.5, 'pm 5'], [5.5, 6, 0, 0.5, 'pm 5'],
                [6, 6, 2, 1, 'c:244222'], [7, 5, 5, 1, 'c:x57775']
            ])
        },
        {
            id: 'clean', group: 'clean', hue: 210,
            guitar: { model: 's', sel: 'n', tone: 0.8, vol: 1, tuning: 'std', pickMm: 120, sympathy: 0.8 },
            pedals: { comp: { on: false }, wah: { on: false }, boost: { on: false }, drive: { on: false } },
            amp: { model: 'clean', gain: 0.35, bass: 0.5, mid: 0.5, treble: 0.6, presence: 0.5, master: 0.5, res: 0.2 },
            cab: { type: '1x12o', mic: 0.45 },
            mod: { on: false }, delay: { on: false },
            reverb: { on: true, type: 'plate', decay: 0.45, mix: 0.2 }, gate: -80, trim: 6.5,
            jam: { groove: 'ballad', bpm: 90, key: 'G', scale: 'major', prog: [['I', 1], ['V', 1], ['vi', 1], ['IV', 1]] },
            riff: R(90, 8, [
                [0, 6, 3, 0.5], [0.5, 4, 0, 0.5], [1, 3, 0, 0.5], [1.5, 2, 0, 0.5], [2, 1, 3, 0.5], [2.5, 2, 0, 0.5], [3, 3, 0, 0.5], [3.5, 4, 0, 0.5],
                [4, 5, 3, 0.5], [4.5, 4, 2, 0.5], [5, 3, 0, 0.5], [5.5, 2, 1, 0.5], [6, 1, 0, 0.5], [6.5, 2, 1, 0.5], [7, 3, 0, 1]
            ], { ring: true })
        }
    ];

    // ────────────────────────── theory helpers ─────────────────────────────
    var NOTE = { C: 0, 'C#': 1, Db: 1, D: 2, 'D#': 3, Eb: 3, E: 4, F: 5, 'F#': 6, Gb: 6, G: 7, 'G#': 8, Ab: 8, A: 9, 'A#': 10, Bb: 10, B: 11 };
    var NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
    /* Semitones above the key. The seven-note scales are the ones chords are
       built from (a chord = every second note of the scale). A pentatonic and
       the blues scale are melodies inside a seven-note scale - their PARENT -
       and take its chords: rock harmony over A minor pentatonic is A natural
       minor's (Am C Dm Em F G).
       The three minors are the three a music school teaches - natural,
       harmonic (raised 7th: the major V and the leading tone of neoclassical
       metal) and melodic (raised 6th and 7th, the jazz form: the same going up
       and down). The seven modes are all here: major = Ionian, natural minor =
       Aeolian. Order = the order of the scale menu (ElectricGuitarPageTest). */
    var SCALES = {
        minpent: [0, 3, 5, 7, 10],
        majpent: [0, 2, 4, 7, 9],
        blues: [0, 3, 5, 6, 7, 10],
        minor: [0, 2, 3, 5, 7, 8, 10],
        harmminor: [0, 2, 3, 5, 7, 8, 11],
        melminor: [0, 2, 3, 5, 7, 9, 11],
        major: [0, 2, 4, 5, 7, 9, 11],
        dorian: [0, 2, 3, 5, 7, 9, 10],
        phrygian: [0, 1, 3, 5, 7, 8, 10],
        lydian: [0, 2, 4, 6, 7, 9, 11],
        mixolydian: [0, 2, 4, 5, 7, 9, 10],
        locrian: [0, 1, 3, 5, 6, 8, 10],
        phrygdom: [0, 1, 4, 5, 7, 8, 10]
    };
    var PARENT = { minpent: 'minor', majpent: 'major', blues: 'minor' };
    /* the seven-note scale a scale's chords and note names come from */
    function heptatonic(id) { return SCALES[PARENT[id]] || (SCALES[id] && SCALES[id].length === 7 ? SCALES[id] : SCALES.minor); }
    var TRIAD = { '4,7': '', '3,7': 'm', '3,6': 'dim', '4,8': 'aug' };
    /* The triad a seven-note scale holds on its k-th note: that note, the one
       two steps up and the one four steps up. In harmonic minor that makes
       V major (E G# B in A) and III augmented (C E G#). */
    function triadAt(h, k) {
        var r = h[k], third = (h[(k + 2) % 7] - r + 12) % 12, fifth = (h[(k + 4) % 7] - r + 12) % 12;
        var q = TRIAD[third + ',' + fifth];
        return q === undefined ? '' : q;
    }
    /* Roman numerals -> semitones above the key, and which letter they own. */
    var DEGREE = { I: 0, bII: 1, II: 2, bIII: 3, III: 4, IV: 5, bV: 6, V: 7, bVI: 8, VI: 9, bVII: 10, VII: 11 };
    var ROMAN = { I: 0, II: 1, III: 2, IV: 3, V: 4, VI: 5, VII: 6 };
    function parseChord(sym) {
        var m = /^(b?)(VII|VI|IV|V|III|II|I|vii|vi|iv|v|iii|ii|i)(.*)$/.exec(sym);
        if (!m) return { deg: 0, idx: 0, q: '5' };
        var up = m[2].toUpperCase();
        var minor = m[2] !== up;
        var deg = DEGREE[(m[1] || '') + up];
        var q = m[3] || (minor ? 'm' : '');
        return { deg: deg, idx: ROMAN[up], q: q, minor: minor };
    }

    /* ── spelling ──────────────────────────────────────────────────────────
       A note is named from the letter its degree owns: the seventh of A
       harmonic minor is G#, never Ab; the third of F minor is Ab, never G#;
       the bVII of G# minor is F#, not the Gb that "a flat degree takes a
       flat" used to print. A key whose scale would need E#, B#, Fb, Cb or a
       double accidental is written from its enharmonic twin, as on paper:
       A# minor reads Bb minor, C# major reads Db major. */
    var LETTERS = 'CDEFGAB', LETTER_PC = [0, 2, 4, 5, 7, 9, 11];
    var TWIN = { 'C#': 'Db', 'D#': 'Eb', 'F#': 'Gb', 'G#': 'Ab', 'A#': 'Bb', Db: 'C#', Eb: 'D#', Gb: 'F#', Ab: 'G#', Bb: 'A#' };
    var AWKWARD = { 'E#': 1, 'B#': 1, Fb: 1, Cb: 1 };
    var FLATS = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B'];
    var FLAT_KEYS = { F: 1, Bb: 1, Eb: 1, Ab: 1, Db: 1, Gb: 1 };
    function pcName(pc, flat) { return (flat ? FLATS : NAMES)[((pc % 12) + 12) % 12]; }
    function onLetter(letter, pc) {
        var acc = ((pc - LETTER_PC[letter]) % 12 + 18) % 12 - 6;
        return acc === 0 ? LETTERS[letter] : acc === 1 ? LETTERS[letter] + '#' : acc === -1 ? LETTERS[letter] + 'b' : null;
    }
    function spellFrom(name, h) {
        var L = LETTERS.indexOf(name.charAt(0)), root = NOTE[name], bad = 0, acc = 0;
        for (var k = 0; k < 7; k++) {
            var n = onLetter((L + k) % 7, (root + h[k]) % 12);
            if (!n) bad += 2; else { if (AWKWARD[n]) bad++; if (n.length > 1) acc++; }
        }
        return { name: name, letter: L, bad: bad, acc: acc, flat: !!FLAT_KEYS[name] };
    }
    function keySpelling(key, scaleId) {
        var h = heptatonic(scaleId), a = spellFrom(key, h), tw = TWIN[key];
        if (!tw) return a;
        var b = spellFrom(tw, h);
        return (b.bad < a.bad || (b.bad === a.bad && b.acc < a.acc)) ? b : a;
    }
    /* the name of pitch class `pc` standing on degree `idx` (0 = the key's
       letter). A root from outside the scale is read, not studied: the
       borrowed bII of Bb minor is shown as B, not Cb. */
    function degreeName(sp, idx, pc, outside) {
        var n = onLetter((sp.letter + idx) % 7, pc);
        return !n || (outside && AWKWARD[n]) ? pcName(pc, sp.flat) : n;
    }
    /* pc -> spelled name for every note of the scale (null elsewhere); a note
       the parent scale lacks (the blues b5) takes the letter of the degree
       above it, flattened: Eb in A blues */
    function scaleNames(key, scaleId) {
        var sp = keySpelling(key, scaleId), h = heptatonic(scaleId), own = SCALES[scaleId] || h, out = [];
        for (var i = 0; i < 12; i++) out.push(null);
        own.forEach(function (iv) {
            var k = h.indexOf(iv);
            if (k < 0) { k = 0; while (k < 7 && h[k] < iv) k++; k = k % 7; }
            var pc = (NOTE[key] + iv) % 12;
            out[pc] = degreeName(sp, k, pc);
        });
        return out;
    }
    var Q_SHOW = { dim: '°', aug: '+' };
    function chordLabel(root, q) { return root + (Q_SHOW[q] !== undefined ? Q_SHOW[q] : q); }
    function chordName(key, sym, scaleId) {
        var c = parseChord(sym), h = heptatonic(scaleId || 'minor');
        var root = degreeName(keySpelling(key, scaleId || 'minor'), c.idx, (NOTE[key] + c.deg) % 12, h.indexOf(c.deg) < 0);
        var q = c.q;
        if (c.minor && q && q !== '5' && q.charAt(0) !== 'm') q = 'm' + q;
        return chordLabel(root, q);
    }

    /* ── the chord pads ────────────────────────────────────────────────────
       The song's chords first (what the band plays), then the scale's own
       chords in the order songs reach for them - tonic, subdominant, dominant,
       then the rest - then a chord the style borrows to fill the row: the
       major V a minor song leans on, the bII of metal, the bVII of a major
       rock song. "Full" builds every chord from the scale - in A harmonic
       minor the V is E major and the VII G#°, not natural minor's Em and G
       (the pads used to read the quality from a fixed minor/major list, so a
       new scale played the old one's chords). The roots are the same in both
       modes: Power/Full changes the voicing, not which chord is on which key. */
    var PRIORITY = [0, 3, 4, 5, 2, 6, 1];
    function padChords(key, scaleId, prog, type, max) {
        max = max || 8;
        var h = heptatonic(scaleId), sp = keySpelling(key, scaleId), keyPc = NOTE[key];
        var minorFamily = h.indexOf(3) >= 0 && h.indexOf(4) < 0;
        var out = [], seen = {};
        function push(deg, idx, q, src) {
            if (seen[deg] || out.length >= max) return;
            seen[deg] = 1;
            var rootPc = (keyPc + deg) % 12, qq = type === '5' ? '5' : q;
            out.push({ deg: deg, rootPc: rootPc, q: qq, name: chordLabel(degreeName(sp, idx, rootPc, h.indexOf(deg) < 0), qq), src: src });
        }
        (prog || []).forEach(function (x) {
            var c = parseChord(x && x[0] !== undefined && typeof x !== 'string' ? x[0] : x), q = c.q;
            if (q === '5') { var k = h.indexOf(c.deg); q = k >= 0 ? triadAt(h, k) : (c.minor ? 'm' : ''); }
            else if (c.minor && (q === '7' || q === '9')) q = 'm' + q;
            push(c.deg, c.idx, q, 'song');
        });
        PRIORITY.forEach(function (k) { push(h[k], k, triadAt(h, k), 'scale'); });
        (minorFamily ? ['V', 'bII', 'bV'] : ['bVII', 'bIII', 'bVI']).forEach(function (sym) {
            var c = parseChord(sym);
            push(c.deg, c.idx, c.q, 'borrowed');
        });
        return out;
    }

    var SYNC = { '1': 4, '2': 2, '4': 1, '8': 0.5, '8.': 0.75, '8t': 1 / 3, '16': 0.25 };
    /*
     * The rig state a preset stands for, ready for EGRig.Rig#apply. The
     * pickup's resonance/output come from the guitar model (eg-core MODELS);
     * a synced delay becomes a time at the jam tempo.
     */
    /* Every knob of every pedal and effect, at the value its knob shows when a
       preset says nothing about it. Presets write only what they change
       (`drive: { on: false, type: 'od' }`), and the engine MERGES a new rig into
       the old one: the grunge drive's 0.75/0.62 used to stay in the sound after a
       switch to hard rock while the knobs read 0.5 - and a reload changed the
       tone, because the saved rig was the short one. The rig handed out is full. */
    var RIG_DEFAULTS = {
        pedals: {
            comp: { on: false, sustain: 0.5, level: 0.5 },
            wah: { on: false, mode: 'auto', sens: 0.5, pos: 0.5 },
            boost: { on: false, drive: 0.2, level: 0.6 },
            drive: { on: false, type: 'od', drive: 0.5, tone: 0.5, level: 0.5 }
        },
        amp: { gain: 0.5, bass: 0.5, mid: 0.5, treble: 0.5, presence: 0.5, res: 0.5, master: 0.5 },
        mod: { on: false, type: 'chorus', rate: 0.4, depth: 0.5, mix: 0.5 },
        delay: { on: false, type: 'analog', time: 0.38, fb: 0.3, mix: 0.2 },
        reverb: { on: false, type: 'plate', decay: 0.5, mix: 0.2 }
    };
    function full(def, v) { return Object.assign({}, def, v || {}); }
    function rigFor(p, bpm, models) {
        var M = models && models[p.guitar.model];
        var sel = M && M.sel[p.guitar.sel];
        var d = full(RIG_DEFAULTS.delay, p.delay);
        if (d.sync) d.time = (60 / (bpm || p.jam.bpm)) * (SYNC[d.sync] || 1);
        var pedals = {};
        Object.keys(RIG_DEFAULTS.pedals).forEach(function (k) { pedals[k] = full(RIG_DEFAULTS.pedals[k], p.pedals && p.pedals[k]); });
        return {
            guitar: { res: sel ? sel.res : { f: 3000, q: 1.5 }, out: sel ? sel.out : 1, tone: p.guitar.tone, vol: p.guitar.vol },
            pedals: JSON.parse(JSON.stringify(pedals)),
            amp: full(RIG_DEFAULTS.amp, p.amp),
            cab: Object.assign({}, p.cab),
            mod: full(RIG_DEFAULTS.mod, p.mod),
            delay: d,
            reverb: full(RIG_DEFAULTS.reverb, p.reverb),
            gate: p.gate,
            trim: p.trim || 0,
            master: 0.8
        };
    }

    return {
        VERSION: 1,
        rigFor: rigFor,
        RIG_DEFAULTS: RIG_DEFAULTS,
        SYNC: SYNC,
        PRESETS: P,
        GROOVES: GROOVES,
        SCALES: SCALES,
        SCALE_IDS: Object.keys(SCALES),
        NOTE: NOTE,
        NAMES: NAMES,
        parseChord: parseChord,
        chordName: chordName,
        pcName: pcName,
        triadAt: triadAt,
        heptatonic: heptatonic,
        keySpelling: keySpelling,
        scaleNames: scaleNames,
        padChords: padChords,
        byId: function (id) { for (var i = 0; i < P.length; i++) if (P[i].id === id) return P[i]; return null; }
    };
}));
